import { useCallback, useSyncExternalStore } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { extensionBridgeStore } from '../store';
import type { SimulationParams, WindowWithExtensionControl } from '../types';

const getExtensionBridge = () => {
	if (typeof window === 'undefined') {
		return undefined;
	}
	return (window as WindowWithExtensionControl).postOffice?.extensionControl;
};

/**
 * Reads per-placement simulation params from the extension bridge only.
 * There is no product programming API for simulation config.
 */
export const useExtensionBridgeSimulationParams = (
	placementId: string,
): SimulationParams | undefined => {
	const isEnabled = fg('post-office_enable_extension-control');
	const subscribe = useCallback(
		(onStoreChange: () => void) => {
			if (!isEnabled) {
				return () => {};
			}
			return getExtensionBridge()?.subscribe(onStoreChange) ?? (() => {});
		},
		[isEnabled],
	);
	const getSnapshot = useCallback(() => {
		if (!isEnabled) {
			return undefined;
		}
		return extensionBridgeStore.getEnabledSimulationParamsSnapshot(placementId);
	}, [isEnabled, placementId]);

	return useSyncExternalStore(subscribe, getSnapshot, () => undefined);
};
